<template>
  <div class="cat-list">
    <!-- État vide -->
    <div v-if="modelValue.length === 0" class="empty-state">
      <p class="empty-state-text">Aucune ligne CAT pour l'instant.</p>
      <p class="empty-state-hint">Cliquez sur <strong>+ Ajouter CAT</strong> ci-dessus pour commencer.</p>

    </div>

    <!-- Liste des lignes -->
    <div v-else class="lines-table">
      <div
        v-for="(line, index) in modelValue"
        :key="index"
        class="line-row cat-row"
        @click="openEditModal(index)"
        title="Cliquer pour modifier"
      >
        <!-- Badge numéro -->
        <span class="line-badge line-badge-cat">{{ getLineNumber(index) }}</span>

        <!-- Infos principales -->
        <div class="line-info">
          <div class="line-info-primary">
            <span class="info-chip">
              <i class="fas fa-door-open chip-icon"></i>
              {{ getRoomTypeLabel(line.roomType) || '—' }}
            </span>
            <span v-if="line.workType" class="info-chip">
              <i class="fas fa-tools chip-icon"></i>
              {{ getWorkTypeLabel(line.workType) || '—' }}
            </span>
          </div>

          <div class="line-info-secondary">
            <!-- Champs de travaux renseignés -->
            <template v-if="line.workType && workFieldsByType[line.workType]">
              <span
                v-for="(field, fieldKey) in getNonCommentFields(line.workType)"
                :key="fieldKey"
                class="info-pill"
              >
                <span class="pill-label">{{ field.label }}</span>
                <span class="pill-value">{{ formatFieldValue(line.workFields[fieldKey], field) || '—' }}</span>
              </span>
            </template>

            <!-- Montant/Durée pour diagnostics -->
            <template v-if="isDiagnosticType(line.roomType)">
              <span v-if="line.montantDiag != null" class="info-pill">
                <span class="pill-label">Montant</span>
                <span class="pill-value">{{ line.montantDiag }} € TTC</span>
              </span>
              <span v-if="line.dureeDiag != null" class="info-pill">
                <span class="pill-label">Durée</span>
                <span class="pill-value">{{ line.dureeDiag }} j</span>
              </span>
            </template>

            <!-- Sinistre -->
            <span v-if="line.sinistre === 'oui'" class="info-pill pill-warning">
              <i class="fas fa-exclamation-triangle chip-icon"></i>
              <span class="pill-value">Sinistre</span>
            </span>

            <!-- Photos -->
            <span v-if="line.photos && line.photos.length > 0" class="info-pill pill-photo">
              <i class="fas fa-camera chip-icon"></i>
              <span class="pill-value">{{ line.photos.length }} photo{{ line.photos.length > 1 ? 's' : '' }}</span>
            </span>
          </div>
        </div>

        <!-- Actions -->
        <div class="line-actions" @click.stop>
          <button
            type="button"
            class="action-btn action-edit"
            @click="openEditModal(index)"
            title="Modifier"
          >
            <i class="fas fa-pencil-alt"></i>
          </button>
          <button
            type="button"
            class="action-btn action-delete"
            @click="removeLine(index)"
            title="Supprimer"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modale d'édition CAT -->
    <ModalCAT
      v-if="editingModal !== null"
      :line="editingModal.line"
      :isEditing="editingModal.isEditing"
      :roomTypes="roomTypes"
      :workTypes="workTypes"
      :workFieldsByType="workFieldsByType"
      @confirm="onModalConfirm"
      @cancel="onModalCancel"
    />
  </div>
</template>

<script>
import ModalCAT from './ModalCAT.vue'

export default {
  name: 'FormCAT',

  components: { ModalCAT },

  props: {
    modelValue: {
      type: Array,
      required: true,
      default: () => []
    },
    roomTypes: {
      type: Object,
      required: true,
      default: () => ({})
    },
    workTypes: {
      type: Object,
      required: true,
      default: () => ({})
    },
    workFieldsByType: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },

  emits: ['update:modelValue'],

  data() {
    return {
      // { line: Object|null, index: Number|null, isEditing: Boolean }
      editingModal: null
    }
  },

  methods: {
    getLineNumber(index) {
      const num = index + 1
      if (num === 1) return '1ère'
      if (num === 2) return '2ème'
      return `${num}ème`
    },

    isDiagnosticType(roomType) {
      return ['diag-electrique', 'daat'].includes(roomType)
    },

    getRoomTypeLabel(key) {
      return this.roomTypes[key] || key
    },

    getWorkTypeLabel(key) {
      return this.workTypes[key] || key
    },

    getNonCommentFields(workType) {
      const fields = this.workFieldsByType[workType]
      if (!fields) return {}
      const result = {}
      for (const [key, value] of Object.entries(fields)) {
        if (!key.includes('commentaires')) result[key] = value
      }
      return result
    },

    formatFieldValue(value, field) {
      if (!value && value !== 0) return null
      if (field.values && field.values[value]) return field.values[value]
      return value
    },

    openNewModal() {
      this.editingModal = {
        line: null,
        index: null,
        isEditing: false
      }
    },

    openEditModal(index) {
      this.editingModal = {
        line: this.modelValue[index],
        index,
        isEditing: true
      }
    },

    onModalConfirm(line) {
      const newLines = [...this.modelValue]
      if (this.editingModal.isEditing && this.editingModal.index !== null) {
        newLines[this.editingModal.index] = line
      } else {
        newLines.push(line)
      }
      this.$emit('update:modelValue', newLines)
      this.editingModal = null
    },

    onModalCancel() {
      this.editingModal = null
    },

    removeLine(index) {
      const newLines = this.modelValue.filter((_, i) => i !== index)
      this.$emit('update:modelValue', newLines)
    },

    // Exposé au parent pour ouvrir la modale depuis le bouton "Ajouter CAT"
    addLine() {
      this.openNewModal()
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.cat-list {
  width: 100%;
}

/* ── État vide ─────────────────────────────────────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  text-align: center;
  gap: 0.4rem;
}

.empty-state-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.empty-state-hint {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}

.empty-state-required {
  margin-top: 0.5rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #dc2626;
  background: #fee2e2;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  i { flex-shrink: 0; }
}

/* ── Table des lignes ─────────────────────────────────────────────────────── */
.lines-table {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.line-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #b2ebf2;
    box-shadow: 0 2px 8px rgba(104, 207, 223, 0.2);
    background: #f0fffe;
  }
}

.cat-row {
  border-left: 3px solid $lightblue;
}

/* Badge numéro */
.line-badge {
  flex-shrink: 0;
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.line-badge-cat {
  background: #b2ebf2;
  color: #006064;
}

/* Infos */
.line-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.line-info-primary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.line-info-secondary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
}

.info-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  background: #e0f7fa;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #006064;
  white-space: nowrap;
}

.chip-icon {
  font-size: 0.7rem;
  opacity: 0.7;
}

.info-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.15rem 0.45rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;

  .pill-label {
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.68rem;
    letter-spacing: 0.2px;
    flex-shrink: 0;
  }

  .pill-value {
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.pill-warning {
  background: #fffbf0;
  border-color: #fcd34d;
  color: #92400e;

  .chip-icon, .pill-value { color: #b45309; }
}

.pill-photo {
  background: #f0f9ff;
  border-color: #bae6fd;

  .chip-icon, .pill-value { color: #0284c7; }
}

/* Actions */
.line-actions {
  display: flex;
  gap: 0.35rem;
  flex-shrink: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background 0.15s, transform 0.1s;

  &:active { transform: scale(0.93); }
}

.action-edit {
  background: #e0f2fe;
  color: #0284c7;

  &:hover { background: #bae6fd; }
}

.action-delete {
  background: #fee2e2;
  color: #dc2626;

  &:hover { background: #fecaca; }
}
</style>
